/**
 * „Dokumente" — die kuratierte Beleg-Liste unter „Fragebögen" (WH 2026-08-21).
 *
 * Die eine Quelle, aus der der Anlagen-Abgleich zieht: `matching` lädt
 * ausschließlich `active`-Zeilen. Der Reiter zeigt sie **schlank** — nur aktive
 * Einträge, eine kompakte Zeile je Beleg: Titel (umbrechend) links, rechts die
 * Badges (Typ/Produkt), die Umschalter „aktuell"/„aktiv" und — falls verknüpft —
 * ein kleines „Open SF". Die SF-Verknüpfung selbst wird kuratiert eingepflegt
 * (kein Bearbeitungsfeld hier); `PATCH` schaltet nur aktuell/aktiv. Kein
 * SF-Schreibpfad (Leitprinzip 2).
 */
import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Loader2, Search } from "lucide-react";
import clsx from "clsx";

import { useT } from "../i18n";
import { katalog } from "../i18n/lang";
import { Card, Chip, EmptyState, Input, Select } from "../ui";
import { useToast } from "../ui/toast";
import { fragebogenApi, type Anlage, type RegistryPatch } from "./api";

export default function DokumentenRegistry() {
  const t = useT().questionnaires;
  const toast = useToast();
  const [liste, setListe] = useState<Anlage[] | null>(null);
  const [suche, setSuche] = useState("");
  const [typ, setTyp] = useState("");
  const [produkt, setProdukt] = useState("");

  useEffect(() => {
    fragebogenApi
      .registry()
      .then(setListe)
      .catch((e) => {
        toast.err(katalog().questionnaires.dokLadenFehler, (e as Error).message);
        setListe([]);
      });
    // toast ist stabil; bewusst nur einmal laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Nur aktive Einträge — inaktive (Vorgänger, Kundenformulare) sind aus dem
  // Abgleich raus und gehören nicht in die Ansicht (WH 2026-08-21).
  const aktive = useMemo(() => (liste ?? []).filter((a) => a.active), [liste]);
  const typen = useMemo(
    () => Array.from(new Set(aktive.map((a) => a.type))).sort(),
    [aktive]
  );
  const produkte = useMemo(
    () =>
      Array.from(
        new Set(aktive.map((a) => a.product_slug).filter((p): p is string => !!p))
      ).sort(),
    [aktive]
  );

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return aktive.filter((a) => {
      if (typ && a.type !== typ) return false;
      if (produkt === "__global" ? a.product_slug !== null : produkt && a.product_slug !== produkt)
        return false;
      if (q && !`${a.title} ${a.seed_id}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [aktive, suche, typ, produkt]);

  const aktualisiere = (upd: Anlage) =>
    setListe((s) => (s ?? []).map((a) => (a.id === upd.id ? upd : a)));

  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-xs text-text-2">
        {t.dokRegistryHinweis}
      </div>

      <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-center">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-3" />
          <Input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder={t.dokSuche}
            className="pl-8"
          />
        </div>
        <Select value={typ} onChange={(e) => setTyp(e.target.value)}>
          <option value="">{t.dokTypAlle}</option>
          {typen.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </Select>
        <Select value={produkt} onChange={(e) => setProdukt(e.target.value)}>
          <option value="">{t.dokProduktAlle}</option>
          <option value="__global">{t.dokGlobal}</option>
          {produkte.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </Select>
      </div>

      {liste === null ? (
        <Card padding="sm" className="flex items-center gap-2 text-sm text-text-2">
          <Loader2 className="h-4 w-4 animate-spin" /> {t.wirdGeladen}
        </Card>
      ) : gefiltert.length === 0 ? (
        <Card padding="none">
          <EmptyState title={t.dokKeine} hint={t.dokKeineHinweis} />
        </Card>
      ) : (
        <>
          <p className="text-xs text-text-3">{t.dokAnzahl(gefiltert.length, aktive.length)}</p>
          <div className="space-y-1.5">
            {gefiltert.map((a) => (
              <RegistryZeile key={a.id} eintrag={a} onChange={aktualisiere} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function RegistryZeile({
  eintrag,
  onChange,
}: {
  eintrag: Anlage;
  onChange: (upd: Anlage) => void;
}) {
  const t = useT().questionnaires;
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const patch = async (p: RegistryPatch) => {
    setBusy(true);
    try {
      onChange(await fragebogenApi.registryKuratieren(eintrag.id, p));
    } catch (e) {
      toast.err(katalog().questionnaires.dokSpeichernFehler, (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card padding="sm" data-registry-eintrag={eintrag.seed_id}>
      {/* Eine Zeile: Titel bricht um, die Steuerelemente bleiben rechts kompakt. */}
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 flex-1 break-words text-sm font-medium">{eintrag.title}</span>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          <Chip>{eintrag.type}</Chip>
          <Chip>{eintrag.product_slug ?? t.dokGlobal}</Chip>
          <SchalterKnopf
            an={eintrag.latest}
            anLabel={t.aktuell}
            ausLabel={t.veraltet}
            titel={t.dokAktuellUmschalten}
            onClick={() => patch({ latest: !eintrag.latest })}
            disabled={busy}
          />
          <SchalterKnopf
            an={eintrag.active}
            anLabel={t.dokAktiv}
            ausLabel={t.dokInaktiv}
            titel={t.dokAktivUmschalten}
            onClick={() => patch({ active: !eintrag.active })}
            disabled={busy}
            stark
          />
          {eintrag.sf_url && (
            <a
              href={eintrag.sf_url}
              target="_blank"
              rel="noopener noreferrer"
              data-sf-link
              title={t.dokOpenSf}
              className="inline-flex items-center gap-1 rounded-md border border-brand-500/40 bg-brand-50 px-2 py-0.5 text-[0.72rem] font-medium text-brand-700 hover:bg-brand-100 dark:bg-brand-700/10"
            >
              <ExternalLink className="h-3 w-3 shrink-0" />
              {t.dokOpenSf}
            </a>
          )}
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-text-3" />}
        </div>
      </div>
    </Card>
  );
}

/** Zweizustands-Knopf (aktuell/veraltet, aktiv/inaktiv) — kein Browser-Dialog,
 *  ein Klick schaltet um. `stark` hebt den Aktiv-Schalter farblich hervor. */
function SchalterKnopf({
  an,
  anLabel,
  ausLabel,
  titel,
  onClick,
  disabled,
  stark,
}: {
  an: boolean;
  anLabel: string;
  ausLabel: string;
  titel: string;
  onClick: () => void;
  disabled?: boolean;
  stark?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={titel}
      aria-pressed={an}
      className={clsx(
        "rounded-md border px-2 py-0.5 text-[0.72rem] font-medium transition-colors disabled:opacity-50",
        an
          ? stark
            ? "border-success/40 bg-success-soft text-success"
            : "border-brand-500/40 bg-brand-50 text-brand-700 dark:bg-brand-700/10"
          : "border-border bg-surface-2 text-text-3"
      )}
    >
      {an ? anLabel : ausLabel}
    </button>
  );
}
